import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, MessageCircle, UserPlus } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import { ASSISTANCE_WHATSAPP, GUIDE_WHATSAPP, GUIDE_WHATSAPP_DISPLAY, REGISTRATION_LINK, waLink } from "../data/siteData";

const STEPS = [
  {
    n: "1",
    title: "Registrati",
    text: "Accedi alla procedura di registrazione tramite il nostro link dedicato.",
    icon: UserPlus,
    action: {
      type: "external",
      href: REGISTRATION_LINK,
      label: "APRI IL CONTO",
      testid: "conto-step1-btn",
      style: "btn-primary",
    },
  },
  {
    n: "2",
    title: "Completa la registrazione",
    text: "Segui le indicazioni durante la procedura e completa tutti i passaggi richiesti.",
    icon: BookOpen,
    extra: "Hai dubbi durante la registrazione?",
    action: {
      type: "internal",
      to: "/guida",
      label: "VEDI LA GUIDA",
      testid: "conto-step2-btn",
      style: "btn-outline",
    },
    extraAction: {
      href: waLink(GUIDE_WHATSAPP, "Ciao, ho un dubbio durante la registrazione del conto gioco."),
      label: "SCRIVICI SU WHATSAPP",
      testid: "conto-step2-whatsapp-btn",
    },
  },
  {
    n: "3",
    title: "Hai bisogno di assistenza?",
    text: "Se hai bisogno di informazioni o assistenza, contattaci.",
    icon: MessageCircle,
    action: {
      type: "external",
      href: waLink(ASSISTANCE_WHATSAPP, "Ciao, ho bisogno di assistenza per l'apertura del conto gioco online."),
      label: "CONTATTACI SU WHATSAPP",
      testid: "conto-step3-btn",
      style: "btn-outline",
    },
  },
];

export default function ContoOnline() {
  return (
    <>
      <SEO
        title="Apri il tuo Conto Gioco Online | Sisal Store"
        description="Apri il tuo conto gioco online in tre semplici passaggi: registrati tramite il nostro link dedicato, completa la procedura e contattaci su WhatsApp per assistenza. Servizio riservato ai maggiorenni."
      />
      <section data-testid="conto-online-page" className="border-b border-emerald-100 bg-gradient-to-br from-emerald-50 to-green-100/60 py-16 sm:py-24">
        <div className="container-x">
          <Reveal>
            <p className="overline-tag">Conto online — riservato ai maggiorenni</p>
            <h1 className="mt-3 max-w-3xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Apri il tuo conto gioco online
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Tre semplici passaggi. E se hai bisogno, il nostro staff è sempre con te.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-x max-w-4xl space-y-6">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <Reveal key={step.n} delay={i * 0.08}>
                <div
                  data-testid={`conto-step-${step.n}`}
                  className="card-soft relative overflow-hidden p-7 sm:p-10"
                >
                  <span className="pointer-events-none absolute -right-4 -top-8 select-none font-display text-[9rem] font-extrabold leading-none text-slate-100 sm:text-[12rem]">
                    {step.n}
                  </span>
                  <div className="relative">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light text-brand-dark">
                      <Icon className="h-6 w-6" />
                    </span>
                    <p className="overline-tag mt-6">Passaggio {step.n}</p>
                    <h2 className="mt-2 font-display text-xl font-extrabold uppercase tracking-tight text-ink sm:text-2xl">
                      {step.title}
                    </h2>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">
                      {step.text}
                    </p>
                    {step.extra && (
                      <p className="mt-6 font-display text-sm font-bold uppercase tracking-widest text-slate-700">
                        {step.extra}
                      </p>
                    )}
                    <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
                      {step.action.type === "external" ? (
                        <a
                          href={step.action.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={step.action.testid}
                          className={`btn ${step.action.style} btn-lg w-full sm:w-auto`}
                        >
                          {step.action.label}
                          <ArrowUpRight className="h-5 w-5" />
                        </a>
                      ) : (
                        <Link to={step.action.to} data-testid={step.action.testid} className={`btn ${step.action.style} btn-lg w-full sm:w-auto`}>
                          {step.action.label}
                          <ArrowUpRight className="h-5 w-5" />
                        </Link>
                      )}
                      {step.extraAction && (
                        <a
                          href={step.extraAction.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={step.extraAction.testid}
                          className="btn btn-primary btn-lg w-full sm:w-auto"
                        >
                          <MessageCircle className="h-5 w-5" />
                          {step.extraAction.label}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}

          <Reveal delay={0.2}>
            <div className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-slate-400 font-display text-sm font-bold text-slate-700">
                18+
              </span>
              <p className="text-xs leading-relaxed text-slate-500 sm:text-sm">
                Il servizio è riservato ai maggiorenni. Il gioco è vietato ai minori di 18 anni e
                può causare dipendenza patologica. Gioca responsabilmente.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
